﻿/********************************************************************
 * Signature Creation Service module
 * Version 2017-08-18
 * Copyright 2016-2017 Vaestorekisterikeskus
 *
 * Permission is hereby granted, free of charge, to any person
 * obtaining a copy of this software and associated documentation
 * files (the "Software"), to deal in the Software without
 * restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the
 * Software is furnished to do so, subject to the following
 * conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 * OTHER DEALINGS IN THE SOFTWARE.
 *
 *********************************************************************/

// Modified from the original vendor module: converted to typed TypeScript and
// promise-based APIs, with lazy service discovery and validated JSON responses.

export interface ScsRequest {
  selector?: {
    keyusages?: string[];
  };
  content: string;
  contentType: 'data' | 'digest';
  hashAlgorithm: string;
  signatureType: string;
  version: string;
}

export interface ScsResponse {
  status: string;
  signature?: string;
  reasonCode?: number;
  reasonText?: string;
}

export interface ScsVersionInfo {
  version: string | number;
  [key: string]: unknown;
}

interface DiscoveredService {
  signUrl: string;
  versionInfo: ScsVersionInfo;
}

const secureUrls = [
  'https://localhost:53952',
  'https://127.0.0.1:53952',
];

const plainUrls = [
  'http://localhost:53951',
  'http://127.0.0.1:53951',
];

const secureLocalhost = ['https://localhost:53952'];
const plainLocalhost = ['http://localhost:53951'];
const discoveryTimeout = 15_000;
const signingTimeout = 200_000;

function timeoutSignal(milliseconds: number): AbortSignal {
  //safari 15 does not support AbortSignal.timeout. browserslist etc should probably take vare of this, but it doesn't seem to work. so let's polyfill it. 
  //polyfill from https://stackoverflow.com/a/50101022
  if (AbortSignal.timeout)
    return AbortSignal.timeout(milliseconds);

  const controller = new AbortController();
  setTimeout(() => controller.abort(), milliseconds);
  return controller.signal;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isScsVersionInfo(value: unknown): value is ScsVersionInfo {
  if (!isRecord(value))
    return false;

  return typeof value.version === 'string' || typeof value.version === 'number';
}

function isScsResponse(value: unknown): value is ScsResponse {
  if (!isRecord(value) || typeof value.status !== 'string')
    return false;

  return (value.signature === undefined || typeof value.signature === 'string')
    && (value.reasonCode === undefined || typeof value.reasonCode === 'number')
    && (value.reasonText === undefined || typeof value.reasonText === 'string');
}

function isLegacyEdge(): boolean {
  return navigator.userAgent.includes('Trident/');
}

function getStoredScsUrl(): string | null {
  try {
    return localStorage.getItem('scsurl');
  } catch {
    return null;
  }
}

function setStoredScsUrl(url: string | null): void {
  try {
    if (url)
      localStorage.setItem('scsurl', url);
    else
      localStorage.removeItem('scsurl');
  } catch {
    // Storage can be unavailable in privacy modes; discovery still works without it.
  }
}

async function fetchVersion(signUrl: string): Promise<ScsVersionInfo> {
  const versionUrl = `${signUrl.slice(0, signUrl.lastIndexOf('/sign'))}/version`;
  const response = await fetch(versionUrl, {
    cache: 'no-cache',
    method: 'GET',
    mode: 'cors',
    signal: timeoutSignal(discoveryTimeout),
  });

  if (!response.ok)
    throw new Error(`SCS version request failed with status ${response.status}`);

  const versionInfo: unknown = await response.json();
  if (!isScsVersionInfo(versionInfo))
    throw new Error('SCS version response is invalid');

  return versionInfo;
}

function firstSuccessful<T>(attempts: Promise<T>[]): Promise<T> {
  return new Promise((resolve, reject) => {
    let failures = 0;

    for (const attempt of attempts) {
      attempt.then(resolve).catch(() => {
        failures++;
        if (failures === attempts.length)
          reject(new Error('SCS service was not found'));
      });
    }
  });
}

export class ScsClient {
  private service: DiscoveredService | null = null;
  private initialization: Promise<DiscoveredService> | null = null;

  async getVersionInfo(): Promise<ScsVersionInfo> {
    return (await this.initialize()).versionInfo;
  }

  async sign(request: ScsRequest): Promise<ScsResponse> {
    const service = await this.initialize();
    const startedAt = Date.now();

    try {
      const response = await fetch(service.signUrl, {
        body: JSON.stringify(request),
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        mode: 'cors',
        signal: timeoutSignal(signingTimeout),
      });

      if (!response.ok)
        throw new Error(`SCS signing request failed with status ${response.status}`);

      const scsResponse: unknown = await response.json();
      if (!isScsResponse(scsResponse))
        throw new Error('SCS signing response is invalid');

      return scsResponse;
    } catch (error) {
      const elapsed = Date.now() - startedAt;
      const timedOut = error instanceof DOMException
        && (error.name === 'AbortError' || error.name === 'TimeoutError');

      if (!timedOut && elapsed < 10_000) {
        this.reset();
        return {
          status: 'failed',
          reasonCode: 500,
          reasonText: 'SCS not responding',
        };
      }

      return {
        status: 'failed',
        reasonCode: 401,
        reasonText: 'Unauthorized: local timeout',
      };
    }
  }

  private async initialize(): Promise<DiscoveredService> {
    if (this.service)
      return this.service;

    if (!this.initialization) {
      this.initialization = this.discover()
        .then(service => {
          this.service = service;
          setStoredScsUrl(service.signUrl);
          return service;
        })
        .catch(error => {
          this.initialization = null;
          throw error;
        });
    }

    return this.initialization;
  }

  private async discover(): Promise<DiscoveredService> {
    const storedUrl = getStoredScsUrl();
    if (storedUrl) {
      try {
        return {
          signUrl: storedUrl,
          versionInfo: await fetchVersion(storedUrl),
        };
      } catch {
        setStoredScsUrl(null);
      }
    }

    const useSecureUrls = window.location.protocol === 'https:';
    let baseUrls = useSecureUrls ? secureUrls : plainUrls;
    if (isLegacyEdge())
      baseUrls = useSecureUrls ? secureLocalhost : plainLocalhost;

    const attempts = baseUrls.map(async baseUrl => {
      const signUrl = `${baseUrl}/sign`;
      return {
        signUrl,
        versionInfo: await fetchVersion(signUrl),
      };
    });

    return firstSuccessful(attempts);
  }

  private reset(): void {
    setStoredScsUrl(null);
    this.service = null;
    this.initialization = null;
  }
}

export default new ScsClient();
